<template>
  <v-container class="pt-0" fluid>
    <!-- Header -->
    <v-row class="align-center">
      <v-col cols="auto">
        <v-card-title class="pt-0 pl-0">
          Media
        </v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 220px;">
        <select-project
          v-model="entity_id"
          label="Select project"
          @input="onProjectInput"
        />
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-select
          v-model="filters.mime_type"
          :items="typeOptions"
          label="Type"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-text-field
          v-model="filters.search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-btn-toggle v-model="viewMode" mandatory>
          <v-btn value="grid" small>
            <v-icon>mdi-view-grid</v-icon>
          </v-btn>
          <v-btn value="list" small>
            <v-icon>mdi-view-list</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-combobox
          v-model="filters.media_type"
          :items="mediaTypeFilterOptions"
          label="Media type"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="auto" class="filter-col">
        <v-select
          v-model="filters.scope"
          :items="scopeOptions"
          label="Scope"
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-btn-toggle v-model="filters.unusedOnly" class="mr-2">
          <v-btn :value="false" small>All</v-btn>
          <v-btn :value="true" small>Unused</v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="auto">
        <v-btn
          small
          outlined
          :loading="isScanning"
          :disabled="!entity_id"
          @click="onScanUsage"
        >
          <v-icon small left>mdi-magnify-scan</v-icon>
          Scan Usage
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          :disabled="!entity_id"
          @click="uploadDialogOpen = true"
        >
          Upload
        </v-btn>
      </v-col>
    </v-row>

    <!-- Bulk actions toolbar -->
    <v-row v-if="selectedFiles.length" class="mt-0">
      <v-col cols="12" class="pt-0">
        <v-toolbar flat dense color="blue-grey lighten-5">
          <span class="subtitle-2">{{ selectedFiles.length }} file(s) selected</span>
          <v-spacer />
          <v-btn small color="primary" class="mr-2" @click="copyDialogOpen = true">
            Copy to...
          </v-btn>
          <v-btn small color="error" outlined :loading="isBulkDeleting" @click="onBulkDelete">
            Delete Selected
          </v-btn>
          <v-btn v-if="filters.unusedOnly" small color="error" class="ml-2" :loading="isBulkDeleting" @click="onDeleteAllUnused">
            Delete All Unused
          </v-btn>
        </v-toolbar>
      </v-col>
    </v-row>

    <!-- Drag-and-drop overlay -->
    <div
      v-if="entity_id && isDragging"
      class="drop-overlay"
      @dragover.prevent
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <div class="drop-overlay-content">
        <v-icon size="64" color="white">mdi-cloud-upload</v-icon>
        <div class="white--text text-h6 mt-2">Drop files to upload</div>
      </div>
    </div>

    <!-- Grid View -->
    <v-row v-if="viewMode === 'grid'">
      <v-col
        v-for="file in filteredItems"
        :key="file.media_file_id"
        cols="2"
      >
        <v-card hover @click="onSelect(file)" class="media-card-selectable" :class="{ 'media-card-selected': isFileSelected(file) }">
          <v-checkbox
            :input-value="isFileSelected(file)"
            class="media-card-checkbox"
            hide-details
            @click.stop="toggleFileSelection(file)"
          />
          <v-img
            v-if="file.mime_type && file.mime_type.startsWith('image/')"
            :src="file.url"
            height="140"
            contain
          />
          <div v-else-if="file.mime_type && file.mime_type.startsWith('video/')" class="media-grid-icon">
            <v-icon size="64" color="grey">mdi-video</v-icon>
          </div>
          <div v-else class="media-grid-icon">
            <v-icon size="64" color="grey">mdi-file-document</v-icon>
          </div>
          <v-card-text class="pb-2">
            <div class="media-card-title">{{ file.filename }}</div>
            <div class="grey--text text-caption">{{ formatFileSize(file.size_bytes) }}</div>
            <v-chip v-if="isGlobal(file)" x-small color="blue" outlined class="mt-1 mr-1">
              <v-icon x-small left>mdi-earth</v-icon> Global
            </v-chip>
            <v-chip
              v-if="file.is_used"
              x-small
              color="green"
              outlined
              class="mt-1"
            >
              Used
            </v-chip>
            <v-chip
              v-else
              x-small
              color="orange"
              outlined
              class="mt-1"
            >
              Unused
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- List View -->
    <v-row v-if="viewMode === 'list'">
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            v-model="selectedFiles"
            :headers="headers"
            :items="filteredItems"
            :loading="isLoading"
            item-key="media_file_id"
            hide-default-footer
            :items-per-page="-1"
            show-select
          >
            <template #item.preview="{ item }">
              <v-img
                v-if="item.mime_type && item.mime_type.startsWith('image/')"
                :src="item.url"
                width="40"
                height="40"
                contain
              />
              <v-icon v-else-if="item.mime_type && item.mime_type.startsWith('video/')">
                mdi-video
              </v-icon>
              <v-icon v-else>
                mdi-file-document
              </v-icon>
            </template>
            <template #item.size_bytes="{ value }">
              {{ formatFileSize(value) }}
            </template>
            <template #item.dimensions="{ item }">
              <span v-if="item.width_px && item.height_px">{{ item.width_px }}x{{ item.height_px }}</span>
              <span v-else>&mdash;</span>
            </template>
            <template #item.is_used="{ item: fileItem, value }">
              <v-chip v-if="isGlobal(fileItem)" x-small color="blue" outlined class="mr-1">
                <v-icon x-small left>mdi-earth</v-icon> Global
              </v-chip>
              <v-chip v-if="value" x-small color="green" outlined>Used</v-chip>
              <v-chip v-else x-small color="orange" outlined>Unused</v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-icon class="mr-2" @click="onEdit(item)">
                mdi-pencil
              </v-icon>
              <v-icon color="error" @click="onDelete(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialogOpen" max-width="600" persistent>
      <v-card>
        <v-card-title>
          Upload Files
          <v-spacer />
          <v-btn icon :disabled="isUploading" @click="uploadDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <!-- Drop zone -->
          <div
            class="upload-dropzone"
            :class="{ 'upload-dropzone-active': isDropzoneActive }"
            @dragover.prevent="isDropzoneActive = true"
            @dragleave.prevent="isDropzoneActive = false"
            @drop.prevent="onDropzoneFiles"
            @click="$refs.fileInput.click()"
          >
            <v-icon size="48" color="grey">mdi-cloud-upload</v-icon>
            <div class="grey--text mt-2">Drag files here or click to select</div>
            <div class="grey--text text-caption">Max 50MB per file</div>
          </div>
          <input
            ref="fileInput"
            type="file"
            multiple
            style="display: none;"
            @change="onFileInputChange"
          />

          <!-- Upload options -->
          <v-row dense class="mt-3">
            <v-col cols="6">
              <v-combobox
                v-model="uploadMediaType"
                :items="mediaTypePresets"
                label="Media type (category)"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="6" class="d-flex align-center">
              <v-checkbox
                v-model="uploadAsGlobal"
                label="Global (all projects)"
                hide-details
                class="mt-0 pt-0"
              />
            </v-col>
          </v-row>

          <!-- Upload queue -->
          <div v-if="uploadQueue.length" class="mt-4">
            <!-- Overall progress for multiple files -->
            <div v-if="uploadQueue.length > 1 && isUploading" class="mb-3">
              <div class="text-caption grey--text">
                Uploading {{ uploadDoneCount }} / {{ uploadQueue.length }} files...
              </div>
              <v-progress-linear
                :value="overallProgress"
                height="6"
                color="primary"
                class="mt-1"
              />
            </div>

            <div
              v-for="(item, idx) in uploadQueue"
              :key="idx"
              class="upload-queue-item mb-2"
              :class="{ 'upload-queue-error': item.status === 'error' }"
            >
              <v-row dense align="center">
                <v-col cols="auto" class="mr-1">
                  <v-icon v-if="item.status === 'done'" color="green" small>mdi-check-circle</v-icon>
                  <v-icon v-else-if="item.status === 'error'" color="error" small>mdi-alert-circle</v-icon>
                  <v-icon v-else-if="item.status === 'uploading'" color="primary" small class="rotating">mdi-loading</v-icon>
                  <v-icon v-else color="grey" small>mdi-clock-outline</v-icon>
                </v-col>
                <v-col>
                  <div class="text-body-2 text-truncate">{{ item.file.name }}</div>
                  <div class="grey--text text-caption">{{ formatFileSize(item.file.size) }}</div>
                </v-col>
                <v-col cols="auto">
                  <v-btn v-if="item.status === 'pending'" icon x-small @click="removeFromQueue(idx)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-progress-linear
                v-if="item.status === 'uploading'"
                :value="item.progress"
                height="4"
                color="primary"
                class="mt-1"
              />
              <div v-if="item.error" class="error--text text-caption">{{ item.error }}</div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <span v-if="uploadQueue.length" class="grey--text text-caption">
            {{ uploadDoneCount }}/{{ uploadQueue.length }} completed
          </span>
          <v-btn v-if="hasFailedUploads" text color="error" @click="retryFailed">
            Retry failed
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="hasPendingUploads"
            color="primary"
            :loading="isUploading"
            @click="startUpload"
          >
            Upload ({{ pendingCount }})
          </v-btn>
          <v-btn
            v-if="!hasPendingUploads && !hasFailedUploads && uploadQueue.length"
            color="primary"
            @click="onUploadDone"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Copy to Dialog -->
    <v-dialog v-model="copyDialogOpen" max-width="500">
      <v-card>
        <v-card-title>Copy {{ selectedFiles.length }} file(s) to project</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <select-project
            v-model="copyTargetEntityId"
            label="Target project"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="copyDialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!copyTargetEntityId" :loading="isCopying" @click="onCopyFiles">
            Copy
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- File Detail Drawer -->
    <drawer-dialog
      v-model="isDetailOpen"
      width="450px"
      max-width="500px"
    >
      <div v-if="detailFile" class="media-detail">
        <drawer-dialog-controls
          :is-loading="isSaving"
          :is-pristine="isDetailPristine"
          @close="isDetailOpen = false"
          @save="onSaveDetail"
        >
          File Details
        </drawer-dialog-controls>

        <div class="media-detail-content">
          <!-- Preview -->
          <v-card flat class="mb-4">
            <v-img
              v-if="detailFile.mime_type && detailFile.mime_type.startsWith('image/')"
              :src="detailFile.url"
              max-height="300"
              contain
              class="grey lighten-4"
            />
            <div v-else class="media-grid-icon">
              <v-icon size="80" color="grey">
                {{ detailFile.mime_type && detailFile.mime_type.startsWith('video/') ? 'mdi-video' : 'mdi-file-document' }}
              </v-icon>
            </div>
          </v-card>

          <!-- Editable fields -->
          <v-card class="mb-4">
            <v-card-title class="subtitle-2 py-2">Editable</v-card-title>
            <v-divider />
            <v-container>
              <v-text-field
                v-model="detailFile.alt_text"
                label="Alt Text"
                dense
                outlined
                hide-details
                class="mb-3"
                @input="isDetailPristine = false"
              />
              <v-textarea
                v-model="detailFile.caption"
                label="Caption"
                dense
                outlined
                hide-details
                rows="2"
                auto-grow
                @input="isDetailPristine = false"
              />
            </v-container>
          </v-card>

          <!-- Read-only fields -->
          <v-card>
            <v-card-title class="subtitle-2 py-2">Info</v-card-title>
            <v-divider />
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td class="grey--text">Filename</td>
                  <td>{{ detailFile.filename }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Storage Key</td>
                  <td class="text-truncate" style="max-width: 250px;">{{ detailFile.storage_key }}</td>
                </tr>
                <tr>
                  <td class="grey--text">URL</td>
                  <td>
                    <span class="text-truncate d-inline-block" style="max-width: 200px; vertical-align: middle;">{{ detailFile.url }}</span>
                    <v-btn icon x-small @click="copyToClipboard(detailFile.url)">
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr>
                  <td class="grey--text">MIME Type</td>
                  <td>{{ detailFile.mime_type }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Size</td>
                  <td>{{ formatFileSize(detailFile.size_bytes) }}</td>
                </tr>
                <tr v-if="detailFile.width_px && detailFile.height_px">
                  <td class="grey--text">Dimensions</td>
                  <td>{{ detailFile.width_px }} x {{ detailFile.height_px }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Status</td>
                  <td>
                    <v-chip v-if="detailFile.is_used" x-small color="green" outlined>Used</v-chip>
                    <v-chip v-else x-small color="orange" outlined>Unused</v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="grey--text">Created</td>
                  <td>{{ detailFile.created }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Updated</td>
                  <td>{{ detailFile.updated }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <!-- Delete -->
          <v-btn
            color="error"
            outlined
            block
            class="mt-4"
            @click="onDeleteDetail"
          >
            Delete File
          </v-btn>
        </div>
      </div>
    </drawer-dialog>

    <!-- Pagination -->
    <v-row v-if="totalPages > 1" justify="center" class="mt-2">
      <v-col cols="auto">
        <v-pagination
          v-model="pagination.page"
          :length="totalPages"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import { cloneDeep } from 'lodash'
import { API } from '@/utils/api'
import { DrawerDialog, DrawerDialogControls } from '@/components/core/DrawerDialog'
import SelectProject from '@/components/core/SelectProject/SelectProject'

export default {
  components: {
    SelectProject,
    DrawerDialog,
    DrawerDialogControls,
  },
  data() {
    return {
      entity_id: null,
      items: [],
      isLoading: false,
      viewMode: 'grid',
      isDragging: false,
      uploadDialogOpen: false,
      isDropzoneActive: false,
      uploadQueue: [],
      isUploading: false,
      uploadConcurrency: 5,
      uploadMediaType: null,
      maxFileSize: 50 * 1024 * 1024,
      mediaTypePresets: ['games', 'banners', 'avatars', 'icons', 'logos', 'backgrounds'],
      detailFile: null,
      isDetailPristine: true,
      isSaving: false,
      selectedFiles: [],
      isScanning: false,
      isBulkDeleting: false,
      copyDialogOpen: false,
      copyTargetEntityId: null,
      isCopying: false,
      uploadAsGlobal: false,
      filters: {
        mime_type: '',
        media_type: null,
        search: '',
        scope: 'all',
        unusedOnly: false,
      },
      pagination: {
        page: 1,
        perpage: 50,
        total: 0,
      },
      typeOptions: [
        { text: 'All', value: '' },
        { text: 'Images', value: 'image/' },
        { text: 'Videos', value: 'video/' },
        { text: 'Documents', value: 'other' },
      ],
      scopeOptions: [
        { text: 'All', value: 'all' },
        { text: 'Project only', value: 'project' },
        { text: 'Global only', value: 'global' },
      ],
      headers: [
        { text: 'Preview', value: 'preview', sortable: false, width: 60 },
        { text: 'Filename', value: 'filename' },
        { text: 'Type', value: 'mime_type', width: 140 },
        { text: 'Size', value: 'size_bytes', width: 100 },
        { text: 'Dimensions', value: 'dimensions', sortable: false, width: 120 },
        { text: 'Status', value: 'is_used', width: 160 },
        { text: 'Created', value: 'created', width: 160 },
        { text: 'Actions', value: 'actions', sortable: false, width: 120 },
      ],
    }
  },
  computed: {
    filteredItems() {
      let result = this.items
      if (this.filters.scope === 'project') {
        result = result.filter(f => f.entity_id && f.entity_id !== 0)
      } else if (this.filters.scope === 'global') {
        result = result.filter(f => !f.entity_id || f.entity_id === 0)
      }
      if (this.filters.search) {
        const q = this.filters.search.toLowerCase()
        result = result.filter(f => (f.filename || '').toLowerCase().includes(q))
      }
      return result
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.perpage)
    },
    hasPendingUploads() {
      return this.uploadQueue.some(i => i.status === 'pending')
    },
    pendingCount() {
      return this.uploadQueue.filter(i => i.status === 'pending').length
    },
    uploadDoneCount() {
      return this.uploadQueue.filter(i => i.status === 'done').length
    },
    hasFailedUploads() {
      return this.uploadQueue.some(i => i.status === 'error')
    },
    overallProgress() {
      if (!this.uploadQueue.length) {
        return 0
      }
      return Math.round((this.uploadDoneCount / this.uploadQueue.length) * 100)
    },
    mediaTypeFilterOptions() {
      const fromItems = [...new Set(this.items.map(f => f.media_type).filter(Boolean))]
      const all = [...new Set([...fromItems, ...this.mediaTypePresets])]
      return all.sort()
    },
    isDetailOpen: {
      get() {
        return !!this.detailFile
      },
      set(val) {
        if (!val) {
          this.detailFile = null
        }
      },
    },
  },
  watch: {
    'filters.mime_type'() {
      this.pagination.page = 1
      this.loadData()
    },
    'filters.media_type'() {
      this.pagination.page = 1
      this.loadData()
    },
    'filters.scope'() {
      this.selectedFiles = []
    },
    'filters.unusedOnly'() {
      this.pagination.page = 1
      this.selectedFiles = []
      this.loadData()
    },
    copyDialogOpen(val) {
      if (val) {
        this.copyTargetEntityId = null
      }
    },
    'pagination.page'() {
      this.loadData()
    },
  },
  mounted() {
    this._onDragOver = (e) => {
      e.preventDefault()
      if (this.entity_id) {
        this.isDragging = true
      }
    }
    document.addEventListener('dragover', this._onDragOver)
  },
  beforeDestroy() {
    document.removeEventListener('dragover', this._onDragOver)
  },
  methods: {
    onProjectInput(val) {
      if (val) {
        this.loadData()
      }
    },
    loadData() {
      if (!this.entity_id) {
        return
      }
      this.isLoading = true
      const params = {
        entity_id: this.entity_id,
        page: this.pagination.page,
        perpage: this.pagination.perpage,
      }
      const endpoint = this.filters.unusedOnly
        ? '/cms/media-file/list-unused.json'
        : '/cms/media-file/list.json'
      if (!this.filters.unusedOnly && this.filters.mime_type && this.filters.mime_type !== 'other') {
        params.mime_type = this.filters.mime_type
      }
      if (this.filters.media_type) {
        params.media_type = this.filters.media_type
      }
      API.call(endpoint, params)
        .then(r => {
          let items = r.data.items || []
          if (this.filters.mime_type === 'other') {
            items = items.filter(f => {
              const mt = f.mime_type || ''
              return !mt.startsWith('image/') && !mt.startsWith('video/')
            })
          }
          this.items = items
          this.pagination.total = (r.data.pagination && r.data.pagination.total) || items.length
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    formatFileSize(bytes) {
      if (!bytes) {
        return '0 B'
      }
      if (bytes < 1024) {
        return bytes + ' B'
      }
      if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB'
      }
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    // Upload methods
    onDrop(e) {
      this.isDragging = false
      const files = Array.from(e.dataTransfer.files)
      if (files.length) {
        this.addToQueue(files)
        this.uploadDialogOpen = true
      }
    },
    onDropzoneFiles(e) {
      this.isDropzoneActive = false
      const files = Array.from(e.dataTransfer.files)
      this.addToQueue(files)
    },
    onFileInputChange(e) {
      const files = Array.from(e.target.files)
      this.addToQueue(files)
      e.target.value = ''
    },
    addToQueue(files) {
      for (const file of files) {
        if (file.size > this.maxFileSize) {
          this.uploadQueue.push({
            file,
            status: 'error',
            progress: 0,
            error: `File too large (${this.formatFileSize(file.size)}). Max ${this.formatFileSize(this.maxFileSize)}`,
          })
          continue
        }
        this.uploadQueue.push({
          file,
          status: 'pending',
          progress: 0,
          error: null,
        })
      }
    },
    removeFromQueue(idx) {
      this.uploadQueue.splice(idx, 1)
    },
    retryFailed() {
      for (const item of this.uploadQueue) {
        if (item.status === 'error') {
          item.status = 'pending'
          item.progress = 0
          item.error = null
        }
      }
    },
    getImageDimensions(file) {
      return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) {
          resolve({ width: 0, height: 0 })
          return
        }
        const img = new Image()
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
          URL.revokeObjectURL(img.src)
        }
        img.onerror = () => {
          resolve({ width: 0, height: 0 })
          URL.revokeObjectURL(img.src)
        }
        img.src = URL.createObjectURL(file)
      })
    },
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsArrayBuffer(file)
      })
    },
    getUploadMode(fileCount, fileSize) {
      const cfg = this.$store.state.auth.cms_upload || {}
      const proxyAllowed = cfg.proxy !== false
      const presignedAllowed = cfg.presigned !== false

      if (!proxyAllowed && !presignedAllowed) {
        return null
      }
      if (!presignedAllowed) {
        return 'proxy'
      }
      if (!proxyAllowed) {
        return 'presigned'
      }
      // Both allowed: 1 file ≤ 20MB → proxy, otherwise presigned
      if (fileCount === 1 && fileSize <= 20 * 1024 * 1024) {
        return 'proxy'
      }
      return 'presigned'
    },
    async startUpload() {
      this.isUploading = true
      const pending = this.uploadQueue.filter(i => i.status === 'pending')
      const mode = this.getUploadMode(pending.length, pending.length === 1 ? pending[0].file.size : 0)

      if (!mode) {
        for (const item of pending) {
          item.status = 'error'
          item.error = 'Upload is disabled'
        }
        this.isUploading = false
        return
      }

      if (mode === 'proxy') {
        for (const item of pending) {
          await this.uploadFileProxy(item)
        }
      } else if (mode === 'presigned') {
        let idx = 0
        const next = async () => {
          while (idx < pending.length) {
            await this.uploadFilePresigned(pending[idx++])
          }
        }
        const workers = []
        for (let i = 0; i < Math.min(this.uploadConcurrency, pending.length); i++) {
          workers.push(next())
        }
        await Promise.all(workers)
      }
      this.isUploading = false
    },
    async uploadFileProxy(item) {
      item.status = 'uploading'
      item.progress = 10
      try {
        const file = item.file
        const entityId = this.uploadAsGlobal ? 0 : this.entity_id
        const buffer = await this.readFileAsArrayBuffer(file)
        item.progress = 40

        const dims = await this.getImageDimensions(file)
        item.progress = 50

        await API.call('/cms/media-file/upload.json', {
          entity_id: entityId,
          media_type: this.uploadMediaType || undefined,
          filename: file.name,
          mime_type: file.type,
          file: Array.from(new Uint8Array(buffer)),
        })

        item.progress = 100
        item.status = 'done'
      } catch (e) {
        item.status = 'error'
        item.error = e.message || 'Upload failed'
      }
    },
    async uploadFilePresigned(item) {
      item.status = 'uploading'
      item.progress = 0
      let mediaFileId = null
      try {
        const file = item.file
        const entityId = this.uploadAsGlobal ? 0 : this.entity_id

        // Step 1: Create DB record (unused, no url yet)
        item.progress = 5
        const createRes = await API.call('/cms/media-file/upsert.json', {
          entity_id: entityId,
          media_type: this.uploadMediaType || undefined,
          filename: file.name,
          mime_type: file.type,
          size_bytes: file.size,
        })
        mediaFileId = createRes.data.item.media_file_id

        // Step 2: Get presigned URL
        item.progress = 10
        const urlRes = await API.call('/cms/media-file/get-upload-url.json', {
          entity_id: entityId,
          media_type: this.uploadMediaType || undefined,
          filename: file.name,
          mime_type: file.type,
        })
        const { upload_url, public_url, storage_key } = urlRes.data

        // Step 3: Upload to S3 via axios
        item.progress = 20
        await axios.put(upload_url, file, {
          headers: { 'Content-Type': file.type },
          onUploadProgress: (e) => {
            if (e.total) {
              item.progress = 20 + Math.round((e.loaded / e.total) * 60)
            }
          },
        })

        // Step 4: Get image dimensions
        item.progress = 85
        const dims = await this.getImageDimensions(file)

        // Step 5: Update DB record with url, storage_key, dimensions
        item.progress = 90
        await API.call('/cms/media-file/upsert.json', {
          media_file_id: mediaFileId,
          entity_id: entityId,
          media_type: this.uploadMediaType || undefined,
          filename: file.name,
          storage_key,
          url: public_url,
          mime_type: file.type,
          size_bytes: file.size,
          width_px: dims.width,
          height_px: dims.height,
        })

        item.progress = 100
        item.status = 'done'
      } catch (e) {
        item.status = 'error'
        item.error = e.message || 'Upload failed'
        // Cleanup: delete orphan DB record
        if (mediaFileId) {
          API.call('/cms/media-file/delete.json', { media_file_id: mediaFileId, entity_id: this.uploadAsGlobal ? 0 : this.entity_id }).catch(() => {})
        }
      }
    },
    onUploadDone() {
      this.uploadDialogOpen = false
      this.uploadQueue = []
      this.loadData()
    },
    isGlobal(file) {
      return !file.entity_id || file.entity_id === 0
    },
    // Copy files
    onCopyFiles() {
      if (!this.selectedFiles.length || !this.copyTargetEntityId) {
        return
      }
      this.isCopying = true
      const ids = this.selectedFiles.map(f => f.media_file_id)
      API.call('/cms/media-file/copy.json', {
        media_file_ids: ids,
        target_entity_id: this.copyTargetEntityId,
        entity_id: this.entity_id,
      }).then(r => {
        const copied = (r.data && r.data.items) ? r.data.items.length : ids.length
        this.$showSuccess(`${copied} file(s) copied successfully`)
        this.copyDialogOpen = false
        this.selectedFiles = []
      }).finally(() => {
        this.isCopying = false
      })
    },
    // Selection helpers for grid
    isFileSelected(file) {
      return this.selectedFiles.some(f => f.media_file_id === file.media_file_id)
    },
    toggleFileSelection(file) {
      const idx = this.selectedFiles.findIndex(f => f.media_file_id === file.media_file_id)
      if (idx >= 0) {
        this.selectedFiles.splice(idx, 1)
      } else {
        this.selectedFiles.push(file)
      }
    },
    // Scan & Bulk delete
    onScanUsage() {
      if (!this.entity_id) {
        return
      }
      this.isScanning = true
      API.call('/cms/media-file/scan-usage.json', { entity_id: this.entity_id })
        .then(r => {
          const total = r.data.total_files || 0
          const unused = r.data.unused_files || 0
          this.$showSuccess(`Scanned ${total} files, ${unused} unused`)
          this.loadData()
        })
        .finally(() => {
          this.isScanning = false
        })
    },
    onBulkDelete() {
      const ids = this.selectedFiles.map(f => f.media_file_id)
      this.$root.$confirm('Confirm', `Delete ${ids.length} file(s)? This cannot be undone.`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isBulkDeleting = true
          API.call('/cms/media-file/bulk-delete.json', { media_file_ids: ids, entity_id: this.entity_id })
            .then(r => {
              const deleted = r.data.deleted || ids.length
              this.$showSuccess(`${deleted} file(s) deleted`)
              this.selectedFiles = []
              this.loadData()
            })
            .finally(() => {
              this.isBulkDeleting = false
            })
        })
    },
    onDeleteAllUnused() {
      const ids = this.filteredItems.map(f => f.media_file_id)
      if (!ids.length) {
        return
      }
      this.$root.$confirm('Confirm', `Delete all ${ids.length} unused file(s)? This cannot be undone.`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isBulkDeleting = true
          API.call('/cms/media-file/bulk-delete.json', { media_file_ids: ids, entity_id: this.entity_id })
            .then(r => {
              const deleted = r.data.deleted || ids.length
              this.$showSuccess(`${deleted} file(s) deleted`)
              this.selectedFiles = []
              this.loadData()
            })
            .finally(() => {
              this.isBulkDeleting = false
            })
        })
    },
    openDetail(file) {
      this.detailFile = cloneDeep(file)
      this.isDetailPristine = true
    },
    onSelect(file) {
      this.openDetail(file)
    },
    onEdit(file) {
      this.openDetail(file)
    },
    onSaveDetail() {
      if (!this.detailFile) {
        return
      }
      this.isSaving = true
      API.call('/cms/media-file/upsert.json', {
        media_file_id: this.detailFile.media_file_id,
        entity_id: this.detailFile.entity_id || 0,
        filename: this.detailFile.filename,
        storage_key: this.detailFile.storage_key,
        mime_type: this.detailFile.mime_type,
        alt_text: this.detailFile.alt_text,
        caption: this.detailFile.caption,
      }).then(() => {
        this.$showSuccess('File updated')
        this.detailFile = null
        this.loadData()
      }).finally(() => {
        this.isSaving = false
      })
    },
    onDeleteDetail() {
      if (!this.detailFile) {
        return
      }
      this.$root.$confirm('Confirm', `Delete file "${this.detailFile.filename}"? This cannot be undone.`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isSaving = true
          API.call('/cms/media-file/delete.json', { media_file_id: this.detailFile.media_file_id, entity_id: this.detailFile.entity_id || 0 })
            .then(() => {
              this.$showSuccess('File deleted')
              this.detailFile = null
              this.loadData()
            })
            .finally(() => {
              this.isSaving = false
            })
        })
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$showSuccess('Copied to clipboard')
      })
    },
    onDelete(file) {
      this.$root.$confirm('Confirm', `Delete file "${file.filename}"?`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isLoading = true
          API.call('/cms/media-file/delete.json', { media_file_id: file.media_file_id, entity_id: file.entity_id || 0 })
            .then(() => {
              this.$showSuccess('File deleted')
              this.loadData()
            })
            .finally(() => {
              this.isLoading = false
            })
        })
    },
  },
}
</script>

<style scoped>
.filter-col {
  min-width: 140px !important;
  max-width: 225px !important;
}
.media-card-title {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.media-grid-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 140px !important;
  background: #f5f5f5 !important;
}
.drop-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(25, 118, 210, 0.85) !important;
  z-index: 9999 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
.drop-overlay-content {
  text-align: center !important;
}
.upload-dropzone {
  border: 2px dashed #ccc !important;
  border-radius: 8px !important;
  padding: 40px !important;
  text-align: center !important;
  cursor: pointer !important;
  transition: border-color 0.2s !important;
}
.upload-dropzone:hover,
.upload-dropzone-active {
  border-color: #1976d2 !important;
  background: #f5f9ff !important;
}
.upload-queue-item {
  border: 1px solid #eee !important;
  border-radius: 4px !important;
  padding: 8px 12px !important;
}
.upload-queue-error {
  border-color: #ff5252 !important;
  background: #fff5f5 !important;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.rotating {
  animation: spin 1s linear infinite !important;
}
.media-card-selectable {
  position: relative !important;
}
.media-card-checkbox {
  position: absolute !important;
  top: 4px !important;
  left: 4px !important;
  z-index: 1 !important;
  margin: 0 !important;
  padding: 0 !important;
}
.media-card-selected {
  outline: 2px solid #1976d2 !important;
}
.media-detail {
  display: flex !important;
  flex-direction: column !important;
  height: 100vh !important;
}
.media-detail-content {
  background-color: #F2F2F2 !important;
  padding: 1rem !important;
  flex: 1 !important;
  overflow-y: auto !important;
}
</style>